import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { Observable, interval, BehaviorSubject, Subscription } from 'rxjs';
import { map, takeWhile } from 'rxjs/operators';

const STORE_KEY = 'userLastAction';

@Injectable({
    providedIn: 'root'
})
export class NgIdleService implements OnDestroy
{
    public static runTimer: boolean;
    public static runSecondTimer: boolean;
    public USER_IDLE_TIMER_VALUE_IN_MIN: number;
    public FINAL_LEVEL_TIMER_VALUE_IN_MIN: number;
    public userIdlenessChecker: BehaviorSubject<string>;
    public secondLevelUserIdleChecker: BehaviorSubject<string>;

    private sessionForIdle: Observable<number>;
    private clockForIdle: Observable<number>;
    private mainTimerSubscription: Subscription;
    private finalTimerSubscription: Subscription;
    private userActivityChangeCallback: EventListener | EventListenerObject;
    private isListenerAttached = false;

    constructor(private zone: NgZone)
    {
        if (!this.userIdlenessChecker)
        {
            this.userIdlenessChecker = new BehaviorSubject<string>('INITIATE_TIMER');
        }

        if (!this.secondLevelUserIdleChecker)
        {
            this.secondLevelUserIdleChecker = new BehaviorSubject<string>('INITIATE_SECOND_TIMER');
        }
    }

    public initilizeSessionTimeout(): void
    {
        NgIdleService.runTimer = true;
        NgIdleService.runSecondTimer = false;

        this.stopMainTimer();
        this.stopFinalTimer();
        this.removeListener();

        if (this.USER_IDLE_TIMER_VALUE_IN_MIN === 0)
        {
            console.warn('NgIdleService: USER_IDLE_TIMER_VALUE_IN_MIN must be greater than 0');
            return;
        }

        this.reset();
        this.initListener();
        this.initInterval();
    }

    get lastAction(): number
    {
        return parseInt(localStorage.getItem(STORE_KEY), 10);
    }

    set lastAction(value)
    {
        localStorage.setItem(STORE_KEY, value.toString());
    }

    private initListener(): void
    {
        if (this.isListenerAttached)
        {
            return;
        }

        this.zone.runOutsideAngular(() =>
        {
            this.userActivityChangeCallback = (event: Event) => this.handleUserActiveState(event);
            window.document.addEventListener('click', this.userActivityChangeCallback as EventListener, true);
            this.isListenerAttached = true;
        });
    }

    private removeListener(): void
    {
        if (!this.isListenerAttached || !this.userActivityChangeCallback)
        {
            return;
        }

        window.document.removeEventListener('click', this.userActivityChangeCallback as EventListener, true);
        this.isListenerAttached = false;
    }

    handleUserActiveState(event: Event): void
    {
        this.reset();
    }

    public reset(): void
    {
        this.lastAction = Date.now();
        if (this.userIdlenessChecker)
        {
            this.userIdlenessChecker.next('RESET_TIMER');
        }
    }

    public stopTimer(): void
    {
        NgIdleService.runTimer = false;
        NgIdleService.runSecondTimer = false;
        this.stopMainTimer();
        this.stopFinalTimer();
        this.removeListener();

        if (this.userIdlenessChecker)
        {
            this.userIdlenessChecker.next('RESET_TIMER');
        }

        if (this.secondLevelUserIdleChecker)
        {
            this.secondLevelUserIdleChecker.next('INITIATE_SECOND_TIMER');
        }
    }

    /**
     * Restart the idle timer after user continues session.
     * This properly resets all timer flags and restarts the interval.
     * Called when user clicks "Continue Session" on the warning modal.
     */
    public restartTimer(): void
    {
        console.log('NgIdleService: Restarting idle timer');

        NgIdleService.runTimer = true;
        NgIdleService.runSecondTimer = false;
        this.stopMainTimer();
        this.stopFinalTimer();
        this.removeListener();

        this.lastAction = Date.now();

        if (this.userIdlenessChecker)
        {
            this.userIdlenessChecker.next('RESET_TIMER');
        }

        if (this.secondLevelUserIdleChecker)
        {
            this.secondLevelUserIdleChecker.next('INITIATE_SECOND_TIMER');
        }

        this.initListener();
        this.initInterval();
    }

    private initInterval(): void
    {
        const intervalDuration = 1000;
        this.sessionForIdle = interval(intervalDuration).pipe(
            map((tick: number) => tick),
            takeWhile(() => NgIdleService.runTimer)
        );

        this.check();
    }

    private check(): void
    {
        this.stopMainTimer();
        this.mainTimerSubscription = this.sessionForIdle.subscribe(() =>
        {
            const now = Date.now();
            const timeleft = this.lastAction + this.USER_IDLE_TIMER_VALUE_IN_MIN * 60 * 1000;
            const diff = timeleft - now;
            const isTimeout = diff < 0;

            if (this.userIdlenessChecker)
            {
                this.userIdlenessChecker.next(`${diff}`);
            }

            if (isTimeout)
            {
                this.stopMainTimer();
                this.removeListener();
                this.zone.run(() =>
                {
                    if (this.userIdlenessChecker)
                    {
                        this.userIdlenessChecker.next('STOPPED_TIMER');

                        if (this.FINAL_LEVEL_TIMER_VALUE_IN_MIN > 0)
                        {
                            this.secondLevelUserIdleChecker.next('SECOND_TIMER_STARTED');
                            this.executeFinalTimer();
                        }
                    }
                    NgIdleService.runTimer = false;
                });
            }
        });
    }

    public removeActionFromStore(): void
    {
        localStorage.removeItem(STORE_KEY);
    }

    private executeFinalTimer = () =>
    {
        NgIdleService.runSecondTimer = true;
        this.initializeFinalTimer();
    }

    private initializeFinalTimer(): void
    {
        this.stopFinalTimer();

        const intervalDuration = 1000;
        this.clockForIdle = interval(intervalDuration).pipe(
            map((tick: number) => tick),
            takeWhile(() => NgIdleService.runSecondTimer)
        );

        this.checkUserActionTime();
    }

    private checkUserActionTime(): void
    {
        let remainingSeconds = this.FINAL_LEVEL_TIMER_VALUE_IN_MIN * 60;
        this.finalTimerSubscription = this.clockForIdle.subscribe(() =>
        {
            remainingSeconds--;

            if (remainingSeconds <= 0)
            {
                NgIdleService.runSecondTimer = false;
                this.stopFinalTimer();

                if (this.secondLevelUserIdleChecker)
                {
                    this.secondLevelUserIdleChecker.next('SECOND_TIMER_STOPPED');
                }
                return;
            }

            const minutes = Math.floor(remainingSeconds / 60);
            const seconds = remainingSeconds % 60;

            if (this.secondLevelUserIdleChecker)
            {
                this.secondLevelUserIdleChecker.next(`${minutes}:${seconds.toString().padStart(2, '0')}`);
            }
        });
    }

    private stopMainTimer(): void
    {
        if (this.mainTimerSubscription)
        {
            this.mainTimerSubscription.unsubscribe();
            this.mainTimerSubscription = undefined;
        }
    }

    private stopFinalTimer(): void
    {
        if (this.finalTimerSubscription)
        {
            this.finalTimerSubscription.unsubscribe();
            this.finalTimerSubscription = undefined;
        }
    }

    ngOnDestroy(): void
    {
        this.stopMainTimer();
        this.stopFinalTimer();
        this.removeListener();

        if (this.userIdlenessChecker)
        {
            this.userIdlenessChecker.complete();
            this.userIdlenessChecker = undefined;
        }

        if (this.secondLevelUserIdleChecker)
        {
            this.secondLevelUserIdleChecker.complete();
            this.secondLevelUserIdleChecker = undefined;
        }
    }
}
